import { useMutation } from '@tanstack/react-query';
import { useState, type SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { samlDiscoverMutation } from '@rootsight/shared';
import { Alert, Button, ErrorNotice, Stack, TextField } from '@rootsight/ui';

import { api } from '../../api';

/**
 * SsoStep asks for a work email, finds the organization that signs in with
 * that email's domain, names it, and sends the browser to that
 * organization's identity provider only after the person confirms it is
 * theirs. The provider is a different site, so going there is a full
 * navigation; the confirm click means nobody is sent to a provider whose
 * owner they did not see.
 */
export function SsoStep({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [leaving, setLeaving] = useState(false);
  const discover = useMutation(samlDiscoverMutation(api));

  const submit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    discover.mutate({ email });
  };

  if (discover.isSuccess) {
    const { loginUrl, organization } = discover.data;
    return (
      <Stack gap={4}>
        <Alert tone="info" title={t('login.sso.ssoConfirmTitle', { organization })}>
          {t('login.sso.ssoConfirm', { email, organization })}
        </Alert>
        <Button
          variant="primary"
          size="lg"
          isPending={leaving}
          onPress={() => {
            setLeaving(true);
            window.location.assign(loginUrl);
          }}
        >
          {t('login.sso.ssoConfirmAction', { organization })}
        </Button>
        <Button variant="ghost" onPress={() => discover.reset()}>
          {t('login.sso.ssoChangeEmail')}
        </Button>
      </Stack>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack gap={4}>
        <p>{t('login.sso.ssoPrompt')}</p>
        {discover.isError && <ErrorNotice error={discover.error} />}
        <TextField
          label={t('login.email')}
          type="email"
          name="email"
          value={email}
          onChange={setEmail}
          placeholder={t('login.emailPlaceholder')}
          autoComplete="username"
          isRequired
          autoFocus
        />
        <Button type="submit" variant="primary" size="lg" isPending={discover.isPending}>
          {t('login.sso.ssoContinue')}
        </Button>
        <Button variant="ghost" onPress={onBack}>
          {t('login.sso.ssoBack')}
        </Button>
      </Stack>
    </form>
  );
}
